/**
 * Admin Pricing Console — `/admin/pricing`
 *
 * Single screen for managing every priced SKU and every platform-fee
 * percentage. Live wired to:
 *   GET    /api/admin/pricing/overview
 *   PATCH  /api/admin/pricing/package/:id   (body: {amount?, disabled?})
 *   DELETE /api/admin/pricing/package/:id   (reset to code default)
 *   PATCH  /api/admin/pricing/platform-fee/:name  (body: {pct})
 *   DELETE /api/admin/pricing/platform-fee/:name
 *
 * Existing active subscriptions keep their locked-in price forever
 * (price-at-purchase is stamped on the subscription doc) — edits only
 * affect new purchases. The UI says this explicitly so the admin
 * doesn't worry about breaking existing customers.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { invalidatePublicPrices } from "@/lib/usePublicPrices";
import { ArrowLeftIcon as ArrowLeft, PencilSimpleIcon as PencilSimple, ArrowCounterClockwiseIcon as ArrowCounterClockwise, EyeSlashIcon as EyeSlash, EyeIcon as Eye, CheckIcon as Check, XIcon as X, ReceiptIcon as Receipt, PercentIcon as Percent, ChartLineUpIcon as ChartLineUp, CurrencyDollarIcon as CurrencyDollar, StorefrontIcon as Storefront, TruckIcon as Truck, HandshakeIcon as Handshake, InfoIcon as Info } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

const FEE_ICONS = { transport: Truck, storefront: Storefront, escrow: Handshake };
const fmtUsd = (v) =>
  Number(v ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const fmtPct = (v, digits = 2) => `${(Number(v ?? 0) * 100).toFixed(digits)}%`;

export default function AdminPricing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/pricing/overview");
      setData(r.data);
      // Refresh the global public-prices cache so every other open tab
      // / page reflects the new amount on next render.
      invalidatePublicPrices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load pricing.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-pricing-page">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-[#6B6B6B] hover:text-ink text-sm mb-4">
        <ArrowLeft size={14} weight="bold" /> Back to admin
      </Link>
      <header className="mb-6">
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight text-ink">Pricing & fees</h1>
        <p className="text-[#6B6B6B] mt-2 max-w-2xl">
          Every priced SKU, every percentage we take. Edits apply to <b>new purchases only</b> — existing active subscriptions keep their locked-in price forever.
        </p>
        <Link
          to="/admin/pricing-reference"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#C9A227] hover:text-ink mt-3"
          data-testid="open-pricing-reference-link"
        >
          <Info size={12} weight="bold" /> Open Pricing Reference (rationale + persona map)
        </Link>
      </header>

      {data && <Kpis totals={data.totals} />}

      <Tabs defaultValue="skus" className="mt-8">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="bg-[#FAFAFA] border border-[#E5E5E5] p-1 rounded-full h-auto whitespace-nowrap" data-testid="pricing-tabs">
            <TabsTrigger value="skus" data-testid="pricing-tab-skus" className="rounded-full px-5 data-[state=active]:bg-ink data-[state=active]:text-white">
              <Receipt size={14} weight="bold" className="mr-1.5" /> Prices ({data?.packages?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="fees" data-testid="pricing-tab-fees" className="rounded-full px-5 data-[state=active]:bg-ink data-[state=active]:text-white">
              <Percent size={14} weight="bold" className="mr-1.5" /> Platform percentages ({data?.platform_fees?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="revenue" data-testid="pricing-tab-revenue" className="rounded-full px-5 data-[state=active]:bg-ink data-[state=active]:text-white">
              <ChartLineUp size={14} weight="bold" className="mr-1.5" /> Revenue by SKU
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="skus" className="mt-6">
          {loading ? <SkeletonRows /> : <SkusTab packages={data?.packages || []} onReload={load} />}
        </TabsContent>

        <TabsContent value="fees" className="mt-6">
          {loading ? <SkeletonRows /> : <FeesTab fees={data?.platform_fees || []} onReload={load} />}
        </TabsContent>

        <TabsContent value="revenue" className="mt-6">
          {loading ? <SkeletonRows /> : <RevenueTab packages={data?.packages || []} totalRev={data?.totals?.revenue_30d_usd || 0} />}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Kpis({ totals }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pricing-kpis">
      <Kpi label="30-day revenue" value={fmtUsd(totals.revenue_30d_usd)} tone="gold" />
      <Kpi label="30-day transactions" value={totals.txn_count_30d.toLocaleString()} />
      <Kpi label="Active subscriptions" value={totals.active_subscriptions.toLocaleString()} />
      <Kpi label="Disabled SKUs" value={totals.disabled_sku_count} tone={totals.disabled_sku_count ? "warn" : "muted"} />
    </div>
  );
}

function Kpi({ label, value, tone = "ink" }) {
  const cls = {
    gold:  "bg-gradient-to-br from-[#FBF7EC] to-[#F7EFD2] border-[#E8D9A6] text-gold-dark",
    warn:  "bg-[#FEF3C7] border-[#FCD34D] text-[#A16207]",
    muted: "bg-white border-[#E5E5E5] text-[#6B6B6B]",
    ink:   "bg-white border-[#E5E5E5] text-ink",
  }[tone];
  return (
    <div className={`rounded-2xl border p-4 ${cls}`}>
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">{label}</p>
      <p className="font-spectral text-3xl tracking-tight tabular-nums mt-1">{value}</p>
    </div>
  );
}

function SkusTab({ packages, onReload }) {
  // Group by category → subcategory
  const groups = useMemo(() => {
    const byCat = new Map();
    for (const p of packages) {
      if (!byCat.has(p.category)) byCat.set(p.category, new Map());
      const sub = byCat.get(p.category);
      if (!sub.has(p.subcategory)) sub.set(p.subcategory, []);
      sub.get(p.subcategory).push(p);
    }
    return byCat;
  }, [packages]);

  return (
    <div className="space-y-8">
      <div className="bg-[#FBF7EC] border border-[#E8D9A6] rounded-xl p-4 text-sm text-gold-dark flex items-start gap-2">
        <Info size={16} weight="duotone" className="mt-0.5 flex-shrink-0" />
        <div>
          <b>How edits work:</b> changing a price affects new purchases starting immediately. Existing active subscriptions keep their original price until they expire — they re-bill at the current price when they renew (or you can let them lapse and force a new purchase at the new price).
        </div>
      </div>

      {[...groups.entries()].map(([cat, subs]) => (
        <section key={cat} data-testid={`pricing-category-${cat.replace(/\s/g, "-").toLowerCase()}`}>
          <h2 className="font-spectral text-2xl tracking-tight text-ink mb-3">{cat}</h2>
          <div className="space-y-6">
            {[...subs.entries()].map(([subcat, rows]) => (
              <div key={subcat}>
                {subcat && <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-2">{subcat}</p>}
                <div className="space-y-2">
                  {rows.map((p) => <SkuRow key={p.package_id} pkg={p} onReload={onReload} />)}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function SkuRow({ pkg, onReload }) {
  const [editing, setEditing] = useState(false);
  const [amountInput, setAmountInput] = useState(String(pkg.current_amount));
  const [saving, setSaving] = useState(false);

  const isOverridden = pkg.override_amount != null;

  const save = async () => {
    const next = parseFloat(amountInput);
    if (isNaN(next) || next < 0) return toast.error("Enter a valid amount.");
    if (Math.abs(next - pkg.default_amount) < 0.005) {
      // Match default exactly → clear override instead of saving identical value.
      return reset();
    }
    setSaving(true);
    try {
      await api.patch(`/admin/pricing/package/${pkg.package_id}`, { amount: next });
      toast.success(`${pkg.name} → ${fmtUsd(next)}`);
      setEditing(false);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.delete(`/admin/pricing/package/${pkg.package_id}`);
      toast.success(`${pkg.name} reset to default (${fmtUsd(pkg.default_amount)}).`);
      setEditing(false);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    } finally {
      setSaving(false);
    }
  };

  const toggleDisabled = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/pricing/package/${pkg.package_id}`, { disabled: !pkg.disabled });
      toast.success(pkg.disabled ? `${pkg.name} re-enabled.` : `${pkg.name} disabled — hidden from checkouts.`);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${pkg.disabled ? "bg-[#FAFAFA] border-[#E5E5E5] opacity-70" : "bg-white border-[#E5E5E5] hover:border-ink/30"}`}
      data-testid={`pricing-row-${pkg.package_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-ink text-[15px]">{pkg.name}</p>
          {pkg.disabled && <Badge className="bg-[#FEF3C7] text-[#A16207] border-0 text-[10px]">Disabled</Badge>}
          {isOverridden && !pkg.disabled && <Badge className="bg-[#FBF7EC] text-gold-dark border-0 text-[10px]">Overridden</Badge>}
          {pkg.days && <Badge className="bg-ink/5 text-ink border-0 text-[10px]">{pkg.days}d</Badge>}
          {pkg.active_subscribers > 0 && (
            <Badge className="bg-[#DCFCE7] text-[#15803D] border-0 text-[10px]">
              {pkg.active_subscribers} active
            </Badge>
          )}
        </div>
        <p className="text-[12px] text-[#6B6B6B] mt-1 line-clamp-2">{pkg.description}</p>
        {pkg.revenue_30d_usd > 0 && (
          <p className="text-[11px] text-gold-dark mt-1 font-medium tabular-nums">
            {fmtUsd(pkg.revenue_30d_usd)} (30d) · {pkg.txn_count_30d} sale{pkg.txn_count_30d === 1 ? "" : "s"}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        {pkg.is_dynamic ? (
          <span className="text-[12px] text-[#6B6B6B] italic">Dynamic per-event price</span>
        ) : editing ? (
          <>
            <span className="text-ink">$</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
              className="w-24 h-9 tabular-nums"
              data-testid={`pricing-amount-input-${pkg.package_id}`}
              autoFocus
            />
            <Button onClick={save} disabled={saving} className="bg-ink text-white rounded-full h-9 px-3" data-testid={`pricing-save-${pkg.package_id}`}>
              <Check size={14} weight="bold" />
            </Button>
            <Button onClick={() => { setEditing(false); setAmountInput(String(pkg.current_amount)); }} variant="ghost" className="rounded-full h-9 px-3">
              <X size={14} weight="bold" />
            </Button>
          </>
        ) : (
          <button
            onClick={() => setEditing(true)}
            disabled={pkg.disabled}
            className="group flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-ink/5 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`pricing-edit-${pkg.package_id}`}
          >
            <span className="font-spectral text-2xl tabular-nums text-ink">{fmtUsd(pkg.current_amount)}</span>
            <PencilSimple size={14} weight="bold" className="text-[#6B6B6B] group-hover:text-ink" />
          </button>
        )}

        {isOverridden && !editing && (
          <button
            onClick={reset}
            disabled={saving}
            title={`Reset to default ${fmtUsd(pkg.default_amount)}`}
            className="p-1.5 rounded-full hover:bg-ink/5 text-[#6B6B6B] hover:text-ink"
            data-testid={`pricing-reset-${pkg.package_id}`}
          >
            <ArrowCounterClockwise size={14} weight="bold" />
          </button>
        )}

        <button
          onClick={toggleDisabled}
          disabled={saving}
          title={pkg.disabled ? "Re-enable this SKU" : "Soft-disable (hide from checkouts; existing subs unaffected)"}
          className={`p-1.5 rounded-full hover:bg-ink/5 ${pkg.disabled ? "text-[#A16207]" : "text-[#6B6B6B] hover:text-crimson"}`}
          data-testid={`pricing-toggle-disabled-${pkg.package_id}`}
        >
          {pkg.disabled ? <Eye size={14} weight="bold" /> : <EyeSlash size={14} weight="bold" />}
        </button>
      </div>
    </div>
  );
}

function FeesTab({ fees, onReload }) {
  return (
    <div className="space-y-4">
      <div className="bg-[#FBF7EC] border border-[#E8D9A6] rounded-xl p-4 text-sm text-gold-dark flex items-start gap-2">
        <Info size={16} weight="duotone" className="mt-0.5 flex-shrink-0" />
        <div>
          <b>Percentage levers</b> are the cut we take on every transaction in that category. Changes apply to new transactions immediately — bookings already in escrow keep their original fee.
        </div>
      </div>
      {fees.map((f) => <FeeRow key={f.name} fee={f} onReload={onReload} />)}
    </div>
  );
}

function FeeRow({ fee, onReload }) {
  const [editing, setEditing] = useState(false);
  const [pctInput, setPctInput] = useState((fee.current_pct * 100).toFixed(2));
  const [saving, setSaving] = useState(false);
  const [previewGross, setPreviewGross] = useState(1500);

  const Icon = FEE_ICONS[fee.name] || Percent;

  const save = async () => {
    const nextPct = parseFloat(pctInput) / 100;
    if (isNaN(nextPct) || nextPct < 0 || nextPct > 1) return toast.error("Enter a percentage between 0 and 100.");
    if (Math.abs(nextPct - fee.default_pct) < 1e-9) return reset();
    setSaving(true);
    try {
      await api.patch(`/admin/pricing/platform-fee/${fee.name}`, { pct: nextPct });
      toast.success(`${fee.label} → ${(nextPct * 100).toFixed(2)}%`);
      setEditing(false);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.delete(`/admin/pricing/platform-fee/${fee.name}`);
      toast.success(`${fee.label} reset to default (${fmtPct(fee.default_pct)}).`);
      setEditing(false);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    } finally {
      setSaving(false);
    }
  };

  const previewFee = previewGross * fee.current_pct;
  const previewNet = previewGross - previewFee;

  return (
    <div
      className={`rounded-2xl border p-5 bg-white ${fee.overridden ? "border-gold/60" : "border-[#E5E5E5]"}`}
      data-testid={`pricing-fee-row-${fee.name}`}
    >
      <div className="flex items-start gap-4">
        <div className="rounded-full bg-gold/15 w-12 h-12 flex items-center justify-center flex-shrink-0">
          <Icon size={22} weight="duotone" className="text-gold-dark" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-ink text-[16px]">{fee.label}</h3>
            {fee.overridden && <Badge className="bg-[#FBF7EC] text-gold-dark border-0 text-[10px]">Overridden · default {fmtPct(fee.default_pct, 0)}</Badge>}
          </div>
          <p className="text-[13px] text-[#3F3F3F] mt-1">{fee.description}</p>
          <p className="text-[11px] text-[#6B6B6B] mt-2 uppercase tracking-[0.16em] font-bold">{fee.applies_to}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {editing ? (
            <>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={pctInput}
                onChange={(e) => setPctInput(e.target.value)}
                className="w-20 h-10 tabular-nums text-right"
                data-testid={`pricing-fee-input-${fee.name}`}
                autoFocus
              />
              <span className="text-ink">%</span>
              <Button onClick={save} disabled={saving} className="bg-ink text-white rounded-full h-10 px-4" data-testid={`pricing-fee-save-${fee.name}`}>
                <Check size={16} weight="bold" />
              </Button>
              <Button onClick={() => { setEditing(false); setPctInput((fee.current_pct * 100).toFixed(2)); }} variant="ghost" className="rounded-full h-10">
                <X size={16} weight="bold" />
              </Button>
            </>
          ) : (
            <>
              <button
                onClick={() => setEditing(true)}
                className="group flex items-center gap-2 px-4 py-2 rounded-full hover:bg-ink/5"
                data-testid={`pricing-fee-edit-${fee.name}`}
              >
                <span className="font-spectral text-3xl tabular-nums text-ink">{fmtPct(fee.current_pct, 2)}</span>
                <PencilSimple size={16} weight="bold" className="text-[#6B6B6B] group-hover:text-ink" />
              </button>
              {fee.overridden && (
                <button
                  onClick={reset}
                  disabled={saving}
                  className="p-2 rounded-full hover:bg-ink/5 text-[#6B6B6B] hover:text-ink"
                  title={`Reset to default ${fmtPct(fee.default_pct, 0)}`}
                  data-testid={`pricing-fee-reset-${fee.name}`}
                >
                  <ArrowCounterClockwise size={14} weight="bold" />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Fee calculator preview */}
      <div className="mt-4 pt-4 border-t border-[#E5E5E5] flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <p className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#6B6B6B] flex items-center gap-1.5">
          <CurrencyDollar size={12} weight="bold" /> Preview · on a $
        </p>
        <Input
          type="number"
          min="0"
          step="50"
          value={previewGross}
          onChange={(e) => setPreviewGross(parseFloat(e.target.value) || 0)}
          className="w-24 h-8 tabular-nums"
        />
        <p className="text-[12px] text-[#3F3F3F] flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>transaction we take <b className="text-ink tabular-nums">{fmtUsd(previewFee)}</b></span>
          <span className="text-[#6B6B6B]">·</span>
          <span>seller / transporter receives <b className="text-ink tabular-nums">{fmtUsd(previewNet)}</b></span>
        </p>
      </div>
    </div>
  );
}

function RevenueTab({ packages, totalRev }) {
  const ranked = [...packages]
    .filter((p) => p.revenue_30d_usd > 0)
    .sort((a, b) => b.revenue_30d_usd - a.revenue_30d_usd);
  const maxRev = ranked[0]?.revenue_30d_usd || 1;

  if (ranked.length === 0) {
    return (
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-8 text-center">
        <p className="font-spectral text-2xl text-ink">No paid transactions in the last 30 days yet</p>
        <p className="text-[#6B6B6B] text-sm mt-2">When sales start flowing, you'll see a ranked breakdown here by SKU.</p>
        <Link to="/admin/accounting" className="inline-block mt-4 text-ink underline font-medium">
          Open Accounting (full P&L) →
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="pricing-revenue-tab">
      <p className="text-[#6B6B6B] text-sm">
        Top-earning SKUs in the last 30 days. Total revenue: <b className="text-ink tabular-nums">{fmtUsd(totalRev)}</b>.
      </p>
      {ranked.map((p) => {
        const widthPct = Math.max(2, (p.revenue_30d_usd / maxRev) * 100);
        return (
          <div key={p.package_id} className="bg-white border border-[#E5E5E5] rounded-xl p-4" data-testid={`pricing-revenue-row-${p.package_id}`}>
            <div className="flex items-baseline justify-between gap-3">
              <p className="font-semibold text-ink text-[14px]">{p.name}</p>
              <p className="font-spectral text-2xl tabular-nums text-ink">{fmtUsd(p.revenue_30d_usd)}</p>
            </div>
            <div className="mt-2 h-2 bg-[#FAFAFA] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-gold to-gold-dark rounded-full" style={{ width: `${widthPct}%` }} />
            </div>
            <p className="text-[11px] text-[#6B6B6B] mt-2">
              {p.txn_count_30d} sale{p.txn_count_30d === 1 ? "" : "s"} · {p.subcategory}
            </p>
          </div>
        );
      })}
      <div className="pt-4">
        <Link to="/admin/accounting" className="text-ink underline font-medium text-sm">
          Open Accounting for the full P&L (Stripe fees, refunds, by-month) →
        </Link>
      </div>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="h-16 bg-[#FAFAFA] rounded-xl animate-pulse" />
      ))}
    </div>
  );
}
